'use client';

/**
 * The arrival question — doc 70 §3.
 *
 * ONE STEP, ASKED ONCE, AND ONLY WHEN IT HAS MORE THAN ONE ANSWER.
 *
 * WHAT THIS REPLACES. A guest arriving at a multi-branch business landed on
 * PlaceHub — a whole page whose only job was picking a branch, with every card
 * a one-way link into the menu and nothing anywhere linking back. From there the
 * mode was never asked at all: it was inferred by a ladder buried in a
 * 3,600-line component, which meant a guest who wanted to look at the food was
 * silently placed in Pickup, and a guest who wanted delivery was too.
 *
 * So: branch and mode are asked together, here, in one sheet, and `resolveArrival`
 * decides which halves are genuine questions. A guest who scanned a table QR is
 * asked nothing and never sees this. A single-branch single-mode business —
 * most of them — never sees it either.
 *
 * THE SAME SHEET IS THE SWITCHER. Opened from the header chip, it is how the
 * guest changes their mind later, which is why the change consequences
 * (`describeModeChange`) render inline rather than in a second confirm dialog.
 * One surface for "how do you want this?", whenever it is asked.
 *
 * @see lib/customer/order-context.ts — the rules this renders
 */

import { useState } from 'react';
import { Check, MapPin } from 'lucide-react';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/components/ui/utils';
import {
  describeModeChange,
  modeDescription,
  modeLabel,
  type OrderMode,
} from '@/lib/customer/order-context';

export type ArrivalBranch = {
  id: string;
  slug: string;
  name: string;
  address?: string | null;
  openState?: 'open' | 'closed' | 'unknown';
};

/**
 * A non-ordering thing the guest might have come to do — reserve a table or
 * plan an event. Rendered in the same list as the ordering modes, but it is a
 * destination, not a mode: confirming navigates away instead of setting a
 * fulfilment context. Only offered when the branch genuinely supports it
 * (MNRQ-UX-02, doc 71) — never as a dead-end link.
 */
export type ArrivalDestination = {
  key: string;
  label: string;
  description: string;
  href: string;
};

export type ArrivalSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessName: string;
  /** Empty or single-entry means the branch half is already settled. */
  branches: readonly ArrivalBranch[];
  selectedBranchId: string | null;
  modes: readonly OrderMode[];
  /** Non-ordering intents this branch can actually serve (reserve/event). */
  destinations?: readonly ArrivalDestination[];
  /** The guest's current mode, when this is being used as the switcher. */
  currentMode: OrderMode | null;
  cartCount: number;
  onConfirm: (choice: {
    branchId: string | null;
    mode: OrderMode | null;
    destination: ArrivalDestination | null;
  }) => void;
  accentColor?: string;
  accentForeground?: string;
};

export function ArrivalSheet({
  open,
  onOpenChange,
  businessName,
  branches,
  selectedBranchId,
  modes,
  destinations = [],
  currentMode,
  cartCount,
  onConfirm,
  accentColor,
  accentForeground,
}: ArrivalSheetProps) {
  const asksBranch = branches.length > 1;
  const [branchId, setBranchId] = useState<string | null>(selectedBranchId);
  const [mode, setMode] = useState<OrderMode | null>(currentMode);
  const [destination, setDestination] = useState<ArrivalDestination | null>(null);

  // Only a REAL switch has consequences. Picking the mode you are already in,
  // or answering for the first time, has none — and a warning shown then would
  // train the guest to dismiss the one that matters.
  const effect =
    currentMode && mode && mode !== currentMode
      ? describeModeChange({ from: currentMode, to: mode, cartCount })
      : null;

  const branchChanged = asksBranch && branchId !== selectedBranchId;
  const ready = Boolean(mode || destination) && (!asksBranch || Boolean(branchId));

  const confirm = () => {
    if (!mode && !destination) return;
    onConfirm({ branchId, mode, destination });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto rounded-t-3xl p-0">
        <SheetHeader className="px-5 pb-2 pt-5 text-left">
          <SheetTitle className="text-lg font-semibold">
            {currentMode ? 'Change how you’re ordering' : businessName}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {currentMode
              ? 'Choose how you want to order from this place.'
              : `Choose how you want to order from ${businessName}.`}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 px-5 pb-6">
          {asksBranch ? (
            <section>
              <h3 className="pb-2 text-sm font-medium text-[var(--color-text-secondary)]">
                Which location?
              </h3>
              <ul className="flex flex-col gap-2">
                {branches.map((branch) => {
                  const selected = branch.id === branchId;
                  return (
                    <li key={branch.id}>
                      <button
                        type="button"
                        onClick={() => setBranchId(branch.id)}
                        aria-pressed={selected}
                        className={cn(
                          'flex min-h-14 w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition',
                          selected
                            ? 'border-[var(--color-text-primary)] bg-[var(--color-bg-raised)]'
                            : 'border-[var(--color-border)] hover:border-[var(--color-border-strong)]',
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-[var(--color-text-primary)]">
                            {branch.name}
                          </p>
                          <p className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)]">
                            {branch.openState && branch.openState !== 'unknown' ? (
                              <span
                                className={
                                  branch.openState === 'open'
                                    ? 'font-medium text-[var(--color-success-text)]'
                                    : 'text-[var(--color-text-tertiary)]'
                                }
                              >
                                {branch.openState === 'open' ? 'Open now' : 'Closed'}
                              </span>
                            ) : null}
                            {branch.address ? (
                              <>
                                {branch.openState && branch.openState !== 'unknown' ? (
                                  <span aria-hidden="true">·</span>
                                ) : null}
                                <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
                                <span className="truncate">{branch.address}</span>
                              </>
                            ) : null}
                          </p>
                        </div>
                        {selected ? (
                          <Check className="size-5 shrink-0 text-[var(--color-text-primary)]" aria-hidden="true" />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          <section>
            <h3 className="pb-2 text-sm font-medium text-[var(--color-text-secondary)]">
              What are you doing today?
            </h3>
            <ul className="flex flex-col gap-2">
              {/* Ordering modes first, then non-ordering destinations, then
                  "Just looking" last — the guest who came to order never has to
                  read past their option (MNRQ-UX-02). */}
              {modes
                .filter((option) => option !== 'browse')
                .map((option) => {
                  const selected = option === mode;
                  return (
                    <li key={option}>
                      <button
                        type="button"
                        onClick={() => {
                          setMode(option);
                          setDestination(null);
                        }}
                        aria-pressed={selected}
                        className={cn(
                          'flex min-h-14 w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition',
                          selected
                            ? 'border-[var(--color-text-primary)] bg-[var(--color-bg-raised)]'
                            : 'border-[var(--color-border)] hover:border-[var(--color-border-strong)]',
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-[var(--color-text-primary)]">
                            {modeLabel(option)}
                          </p>
                          <p className="text-sm text-[var(--color-text-secondary)]">
                            {modeDescription(option)}
                          </p>
                        </div>
                        {selected ? (
                          <Check className="size-5 shrink-0 text-[var(--color-text-primary)]" aria-hidden="true" />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              {destinations.map((option) => {
                const selected = destination?.key === option.key;
                return (
                  <li key={option.key}>
                    <button
                      type="button"
                      onClick={() => {
                        setDestination(option);
                        setMode(null);
                      }}
                      aria-pressed={selected}
                      className={cn(
                        'flex min-h-14 w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition',
                        selected
                          ? 'border-[var(--color-text-primary)] bg-[var(--color-bg-raised)]'
                          : 'border-[var(--color-border)] hover:border-[var(--color-border-strong)]',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[var(--color-text-primary)]">
                          {option.label}
                        </p>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                          {option.description}
                        </p>
                      </div>
                      {selected ? (
                        <Check className="size-5 shrink-0 text-[var(--color-text-primary)]" aria-hidden="true" />
                      ) : null}
                    </button>
                  </li>
                );
              })}
              {modes.includes('browse') ? (
                <li key="browse">
                  <button
                    type="button"
                    onClick={() => {
                      setMode('browse');
                      setDestination(null);
                    }}
                    aria-pressed={mode === 'browse'}
                    className={cn(
                      'flex min-h-14 w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition',
                      mode === 'browse'
                        ? 'border-[var(--color-text-primary)] bg-[var(--color-bg-raised)]'
                        : 'border-[var(--color-border)] hover:border-[var(--color-border-strong)]',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[var(--color-text-primary)]">
                        {modeLabel('browse')}
                      </p>
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        {modeDescription('browse')}
                      </p>
                    </div>
                    {mode === 'browse' ? (
                      <Check className="size-5 shrink-0 text-[var(--color-text-primary)]" aria-hidden="true" />
                    ) : null}
                  </button>
                </li>
              ) : null}
            </ul>
          </section>

          {/* Said BEFORE the switch, not discovered after it. The cart is scoped
              by branch, so without this a pickup cart quietly became a delivery
              cart and the guest met an empty address field at checkout. */}
          {effect && !effect.silent && effect.warning ? (
            <p className="rounded-xl bg-[var(--color-warning-soft)] px-3 py-2.5 text-sm text-[var(--color-warning)]">
              {effect.warning}
            </p>
          ) : null}
          {branchChanged && cartCount > 0 ? (
            <p className="rounded-xl bg-[var(--color-warning-soft)] px-3 py-2.5 text-sm text-[var(--color-warning)]">
              A different location has its own menu and prices, so your cart will start fresh.
            </p>
          ) : null}

          <button
            type="button"
            disabled={!ready}
            onClick={confirm}
            className="flex h-13 min-h-12 w-full items-center justify-center rounded-2xl px-5 text-base font-bold transition active:scale-[0.98] disabled:opacity-40"
            style={{
              backgroundColor: accentColor || 'var(--color-text-primary)',
              color: accentForeground || 'var(--color-bg-base)',
            }}
          >
            {destination
              ? `Continue to ${destination.label.toLowerCase()}`
              : mode === 'browse'
              ? 'Start browsing'
              : mode
              ? `Continue with ${modeLabel(mode).toLowerCase()}`
              : 'Choose an option'}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
